// ===========================================
// SUPPLY PLAN (Build 3)
// ===========================================
// Works backwards from the forecast to what you actually have to press and buy:
// forecast cases → gallons of juice to press → cans/bottles to order. Every ratio
// comes from the maker's own Demand Forecast (settings.supplyRatios); nothing invented.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Droplet, Package, Factory } from 'lucide-react';
import { restaurantApi } from '../services/api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const n = (v: number) => Math.round(v).toLocaleString('en-US');

type Ratio = { galPerCase: number; unitsPerCase: number; container: string; casesPerPallet?: number };
type Sku = { name: string; remaining?: number; fy?: number; monthly?: number[] };

export default function SupplyPlan() {
  const { data: restaurant } = useQuery({ queryKey: ['restaurant-forecast'], queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null });
  const forecast = restaurant?.settings?.forecast as { skus?: Record<string, Sku>; updatedMonth?: number } | undefined;
  // Next year's plan (when loaded) unlocks the harvest view: pressing season runs
  // Sep–Dec and has to cover demand through next September, so the real "what to
  // press" number is rest-of-this-year + Jan–Sep of next year.
  const next = restaurant?.settings?.forecast2027 as { skus?: Record<string, Sku> } | undefined;
  const ratios = (restaurant?.settings?.supplyRatios || {}) as Record<string, Ratio>;
  const updatedMonth = forecast?.updatedMonth ?? 8; // last month with actuals

  // period: 'remaining' = rest of the year, 'harvest' = through next September,
  // or a specific month index
  const [period, setPeriod] = useState<'remaining' | 'harvest' | 'y2027' | number>('remaining');
  const monthOptions = MONTHS.map((_, i) => i).filter((i) => i >= updatedMonth);
  // how to show the packaging-order number: whole pallets (how you'd order) or raw cans/bottles
  const anyPallets = Object.values(ratios).some((r) => r.casesPerPallet);
  const [unit, setUnit] = useState<'pallets' | 'containers'>(anyPallets ? 'pallets' : 'containers');

  const casesFor = (s: Sku, code: string) => {
    if (period === 'remaining') return s.remaining ?? 0;
    if (period === 'harvest') {
      const nextMonthly = next?.skus?.[code]?.monthly || [];
      const janToSep = nextMonthly.slice(0, 9).reduce((a, b) => a + (b || 0), 0);
      return (s.remaining ?? 0) + janToSep;
    }
    if (period === 'y2027') return (next?.skus?.[code]?.monthly || []).reduce((a, b) => a + (b || 0), 0);
    return (s.monthly?.[period as number] ?? 0);
  };

  const rows = Object.entries(forecast?.skus || {})
    .map(([code, s]) => {
      const r = ratios[code];
      const cases = Math.max(0, casesFor(s, code));
      return {
        code, name: s.name, cases,
        gallons: r ? cases * r.galPerCase : null,
        units: r ? Math.round(cases * r.unitsPerCase) : null,
        pallets: r?.casesPerPallet ? Math.ceil(cases / r.casesPerPallet) : null,
        container: r?.container || 'units',
      };
    })
    .filter((x) => x.cases > 0)
    .sort((a, b) => (b.gallons ?? 0) - (a.gallons ?? 0));

  const totalCases = rows.reduce((s, r) => s + r.cases, 0);
  const totalGallons = rows.reduce((s, r) => s + (r.gallons ?? 0), 0);
  const totalPallets = rows.reduce((s, r) => s + (r.pallets ?? 0), 0);
  const unitsByContainer = rows.reduce((m, r) => { m[r.container] = (m[r.container] || 0) + (r.units ?? 0); return m; }, {} as Record<string, number>);
  // per-row + total display for the packaging-order column, respecting the unit toggle
  const packRow = (r: typeof rows[number]) =>
    unit === 'pallets'
      ? (r.pallets != null ? `${n(r.pallets)} pallet${r.pallets === 1 ? '' : 's'}` : '—')
      : (r.units != null ? `${n(r.units)} ${r.container}` : '—');
  const packTotal = unit === 'pallets'
    ? `${n(totalPallets)} pallets`
    : Object.entries(unitsByContainer).map(([c, v]) => `${n(v)} ${c}`).join(' · ');

  if (!forecast?.skus || Object.keys(ratios).length === 0) {
    return <div className="card p-8 text-center text-slate-400">A supply plan appears once your forecast and per-case yields are set up.</div>;
  }

  const periodLabel = period === 'remaining' ? 'the rest of the year'
    : period === 'harvest' ? 'the pressing season (rest of 2026 + demand through Sep 2027)'
    : period === 'y2027' ? 'all of 2027'
    : `${MONTHS[period as number]}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">What to press and order for <span className="font-medium text-slate-700">{periodLabel}</span>, from your forecast.</p>
        <div className="flex items-center gap-2">
          {anyPallets && (
            <div className="flex rounded-lg bg-slate-100 p-0.5 text-xs">
              <button onClick={() => setUnit('pallets')} className={unit === 'pallets' ? 'px-2.5 py-1 rounded-md bg-white shadow-sm font-medium' : 'px-2.5 py-1 text-slate-500'}>Pallets</button>
              <button onClick={() => setUnit('containers')} className={unit === 'containers' ? 'px-2.5 py-1 rounded-md bg-white shadow-sm font-medium' : 'px-2.5 py-1 text-slate-500'}>Cans &amp; bottles</button>
            </div>
          )}
          <select value={String(period)} onChange={(e) => setPeriod(['remaining', 'harvest', 'y2027'].includes(e.target.value) ? e.target.value as any : Number(e.target.value))} className="input w-auto text-sm">
            <option value="remaining">Rest of year</option>
            {next?.skus && <option value="harvest">Harvest press plan (→ Sep 2027)</option>}
            {next?.skus && <option value="y2027">All of 2027</option>}
            {monthOptions.map((i) => <option key={i} value={i}>{MONTHS[i]}</option>)}
          </select>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div className="card p-3"><div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1"><Factory className="w-3.5 h-3.5" /> Cases to make</div><p className="text-2xl font-bold text-slate-900">{n(totalCases)}</p></div>
        <div className="card p-3"><div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1"><Droplet className="w-3.5 h-3.5" /> Gallons to press</div><p className="text-2xl font-bold text-slate-900">{n(totalGallons)}</p></div>
        <div className="card p-3"><div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1"><Package className="w-3.5 h-3.5" /> {unit === 'pallets' ? 'Pallets to order' : 'Containers to order'}</div><p className="text-2xl font-bold text-slate-900">{unit === 'pallets' ? n(totalPallets) : n(Object.values(unitsByContainer).reduce((a, b) => a + b, 0))}</p><p className="text-xs text-slate-400">{unit === 'pallets' ? Object.entries(unitsByContainer).map(([c, v]) => `${n(v)} ${c}`).join(' · ') : ''}</p></div>
      </div>

      {/* Per-SKU cascade */}
      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="px-4 py-2 font-medium">Product</th>
              <th className="px-4 py-2 font-medium text-right">Cases</th>
              <th className="px-4 py-2 font-medium text-right">Gallons to press</th>
              <th className="px-4 py-2 font-medium text-right">{unit === 'pallets' ? 'Pallets to order' : 'Containers to order'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map((r) => (
              <tr key={r.code}>
                <td className="px-4 py-2.5 font-medium text-slate-900">{r.name}</td>
                <td className="px-4 py-2.5 text-right text-slate-600">{n(r.cases)}</td>
                <td className="px-4 py-2.5 text-right text-slate-600">{r.gallons != null ? n(r.gallons) : <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-2.5 text-right text-slate-600">{packRow(r)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-100 font-semibold text-slate-900">
              <td className="px-4 py-2.5">Total</td>
              <td className="px-4 py-2.5 text-right">{n(totalCases)}</td>
              <td className="px-4 py-2.5 text-right">{n(totalGallons)} gal</td>
              <td className="px-4 py-2.5 text-right">{packTotal}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
